<script setup lang="ts">
import AppAvatarCropperModal from '@/components/AppAvatarCropperModal/AppAvatarCropperModal.vue';
import { useToast } from '@/use/toast';
import { useI18n } from '@/i18n';
import { GOOGLE_CLIENT_ID } from '@/lib/env';
import { loadGoogleIdentity, type GoogleCredentialResponse } from '@/lib/googleIdentity';
import { isPasswordlessAccount } from '@/lib/providers';
import { refreshCurrentUser } from '@/lib/refreshUser';
import { uploadAvatarImage } from '@/lib/uploadImage';
import router from '@/router';
import { createProfileSchema, type ProfileFormValues } from '@/schemas/profile';
import { useUserStore } from '@/stores/user';
import { Alert, Avatar, Button, Divider, Form, Heading, Input, LayoutGap, Modal, Prose } from '@dabble/quillui';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, onMounted, ref, watch } from 'vue';

const { t, te, locale } = useI18n();
const userStore = useUserStore();
const { showToast } = useToast();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}

const isDeleteModalOpen = ref(false);
const isLoading = ref(false);
const isCropperOpen = ref(false);
const isApplyingAvatar = ref(false);
const isSendingVerification = ref(false);

/**
 * SSO-only accounts get their profile from the identity provider. The email
 * is the account's link to that provider, so it stays read-only here; name
 * and photo remain editable overrides.
 */
const isSsoManaged = computed(() => isPasswordlessAccount(userStore.user));

// --- "Verify your email" ---
// Password accounts can use Dabble without verifying the address signup emailed
// a link to. Surface a resend affordance while it's unverified — it also clears
// the EMAIL_NOT_VERIFIED gate that blocks linking Google. SSO-managed accounts
// get their (already-verified) email from Google, so they never see this.
const showEmailVerification = computed(() => !isSsoManaged.value && !userStore.user.emailVerified);

async function onSendVerification() {
  if (isSendingVerification.value) return;
  isSendingVerification.value = true;
  try {
    await userStore.sendVerification();
    showToast('success', t('account_verify_email_sent_toast'));
  } catch {
    showToast('danger', t('account_verify_email_failed_toast'));
  } finally {
    isSendingVerification.value = false;
  }
}

// --- "Connect Google" (Sign-in Methods) ---
// Only shown to email/password accounts that haven't linked Google yet — the
// nudge toward SSO. Google-connected accounts see nothing here (the locked
// email + note already says the profile is Google-managed), and unknown
// providers (older API responses) fail safe to hidden.
const showSignInMethods = computed(() => {
  const providers = userStore.user.providers;
  return Array.isArray(providers) && providers.includes('password') && !providers.includes('google.com');
});

/**
 * Which Google identity is attached (Model A linking: Google is an additional
 * sign-in method; the account email is untouched and may differ). Shown for
 * linked password accounts — SSO-only accounts already get the
 * managed-by-Google note instead.
 */
const connectedGoogleEmail = computed(() =>
  isSsoManaged.value ? undefined : userStore.user.providerEmails?.['google.com'],
);
const googleButtonEl = ref<HTMLElement | null>(null);
const googleButtonMounted = ref(false);
const googleButtonFailed = ref(false);
const isLinkingGoogle = ref(false);

// The button container only exists once the user has loaded without a linked
// Google account, so mount when the element appears rather than onMounted.
watch(googleButtonEl, (el) => {
  if (el && !googleButtonMounted.value) {
    googleButtonMounted.value = true;
    mountConnectGoogleButton(el);
  }
});

async function mountConnectGoogleButton(el: HTMLElement) {
  try {
    const accountsId = await loadGoogleIdentity();
    accountsId.initialize({ client_id: GOOGLE_CLIENT_ID, callback: onGoogleLinkCredential, context: 'use' });
    const width = Math.min(400, Math.max(200, Math.round(el.offsetWidth) || 0));
    accountsId.renderButton(el, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      logo_alignment: 'center',
      locale: locale.value,
      ...(width >= 200 ? { width } : {}),
    });
  } catch {
    // GIS blocked (ad blocker, offline): hide the affordance silently — same
    // convention as the auth pages.
    googleButtonFailed.value = true;
  }
}

async function onGoogleLinkCredential(response: GoogleCredentialResponse) {
  const credential = response?.credential;
  if (!credential || isLinkingGoogle.value) return;
  isLinkingGoogle.value = true;
  try {
    await userStore.linkGoogle(credential);
    showToast('success', t('account_google_connected_toast'));
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    showToast('danger', message === 'GOOGLE_ACCOUNT_IN_USE' ? t('error_google_account_in_use') : t('error_sso_failed'));
  } finally {
    isLinkingGoogle.value = false;
  }
}

const rawProfileSchema = computed(() => createProfileSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawProfileSchema.value));

const { handleSubmit, defineField, values, resetForm, validateField } = useForm<ProfileFormValues>({
  validationSchema,
  initialValues: {
    name: '',
    email: '',
  },
});

const isValid = computed(() =>
  rawProfileSchema.value.isValidSync({
    name: values.name ?? '',
    email: values.email ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [name, nameField] = defineField('name', fieldOpts);
const [email, emailField] = defineField('email', fieldOpts);

onMounted(async () => {
  if (userStore.user.uid) {
    // An expired session surfaces here as a 401 and redirects to login; bail
    // out rather than resetting the form against a half-loaded user.
    const refreshed = await refreshCurrentUser(userStore.user.uid);
    if (!refreshed) return;
  }
  const user = userStore.user;
  resetForm({
    values: {
      name: user.name || '',
      email: user.email || '',
    },
  });
});

function openDeleteModal() {
  isDeleteModalOpen.value = true;
}

function closeDeleteModal() {
  isDeleteModalOpen.value = false;
}

async function saveProfile(nextName: string, nextEmail: string) {
  isLoading.value = true;
  try {
    await userStore.updateAccount({ name: nextName, email: nextEmail });
    resetForm({ values: { name: nextName, email: nextEmail } });
    showToast('success', t('account_updated'));
  } catch (error: any) {
    // updateAccount throws before mutating the store, so userStore.user.{name,email}
    // still hold the pre-attempt values — roll the inputs back so the user sees
    // their change has not been saved instead of a silently-stale input.
    resetForm({
      values: {
        name: userStore.user.name,
        email: userStore.user.email,
      },
    });
    showToast('danger', error.message);
  } finally {
    isLoading.value = false;
  }
}

const onSubmit = handleSubmit(async (formValues) => {
  await saveProfile(formValues.name || '', formValues.email || '');
});

async function autoSaveField(field: 'name' | 'email') {
  if (isLoading.value) return;
  const result = await validateField(field);
  if (!result.valid) return;

  const nextName = name.value || '';
  const nextEmail = email.value || '';
  const nameUnchanged = nextName === (userStore.user.name || '');
  const emailUnchanged = nextEmail === (userStore.user.email || '');
  if (nameUnchanged && emailUnchanged) return;

  await saveProfile(nextName, nextEmail);
}

async function onAvatarApply(blob: Blob) {
  const uid = userStore.user.uid;
  if (!uid) return;
  isApplyingAvatar.value = true;
  try {
    const url = await uploadAvatarImage(blob, uid);
    await userStore.updateAccount({
      name: userStore.user.name || '',
      email: userStore.user.email || '',
      photoUrl: url,
    });
    showToast('success', t('account_avatar_updated'));
    isCropperOpen.value = false;
  } catch (error: any) {
    showToast('danger', t('account_avatar_upload_failed'));
  } finally {
    isApplyingAvatar.value = false;
  }
}

async function onAvatarRemove() {
  try {
    await userStore.updateAccount({
      name: userStore.user.name || '',
      email: userStore.user.email || '',
      photoUrl: '',
    });
    showToast('success', t('account_avatar_removed'));
  } catch (error: any) {
    showToast('danger', error.message);
  }
}

async function handleDeleteUser() {
  try {
    await userStore.deleteUser(userStore.user);
    closeDeleteModal();
    showToast('success', t('account_deleted'));
    router.push('/auth/signup');
  } catch (error: any) {
    showToast('danger', error.message);
  }
}
</script>

<template>
  <LayoutGap direction="column" align="stretch" size="medium" fill class="profile-view">
    <LayoutGap direction="column" align="stretch" size="xsmall" fill>
      <Heading :level="1" family="secondary" :margin="false">{{ t('account_profile_title') }}</Heading>
      <Prose variant="secondary">{{ t('account_profile_lede') }}</Prose>
    </LayoutGap>

    <LayoutGap direction="column" align="stretch" size="small" fill class="profile-view__content">
      <LayoutGap direction="row" align="left" vertical-align="center" size="small" class="profile-view__avatar">
        <Avatar
          :key="userStore.user.photoUrl || 'no-photo'"
          :url="userStore.user.photoUrl || undefined"
          :name="userStore.user.name"
          :email="userStore.user.email"
          size="xxhuge"
        />
        <LayoutGap direction="column" align="left" size="xsmall">
          <Button
            type="button"
            variant="secondary"
            finish="outlined"
            icon-left="edit"
            :disabled="isApplyingAvatar"
            @click="isCropperOpen = true"
          >
            {{ t('account_avatar_change') }}
          </Button>
          <Button
            v-if="userStore.user.photoUrl"
            type="button"
            variant="secondary"
            finish="outlined"
            :disabled="isApplyingAvatar"
            @click="onAvatarRemove"
          >
            {{ t('account_avatar_remove') }}
          </Button>
          <Prose variant="secondary" size="xsmall">{{ t('account_avatar_hint') }}</Prose>
        </LayoutGap>
      </LayoutGap>
      <Form v-model:dirty="isDirty" :valid="isValid" :actions="false" class="profile-view__form" @submit="onSubmit">
        <LayoutGap direction="column" align="stretch" size="small" fill>
          <Input
            id="profile-name"
            v-model="name"
            v-bind="nameField"
            :placeholder="t('account_placeholder_name')"
            :label="t('name')"
            type="text"
            required
            @focusout="autoSaveField('name')"
          />
          <Input
            id="profile-email"
            v-model="email"
            v-bind="emailField"
            :label="t('email')"
            name="email"
            type="text"
            required
            :disabled="isSsoManaged"
            @focusout="autoSaveField('email')"
          />
          <Alert v-if="isSsoManaged" variant="info" size="small" class="profile-view__sso-note">
            {{ t('account_profile_google_managed') }}
          </Alert>
          <Alert v-else-if="connectedGoogleEmail" variant="info" size="small" class="profile-view__sso-note">
            {{ t('account_google_connected_as', { email: connectedGoogleEmail }) }}
          </Alert>
          <Button fill-width class="profile-view__submit" type="submit" :disabled="isLoading || !isValid || !isDirty">
            {{ isLoading ? t('account_update_updating') : t('account_update_submit') }}
          </Button>
        </LayoutGap>
      </Form>
      <LayoutGap
        v-if="showEmailVerification"
        direction="column"
        align="stretch"
        size="xsmall"
        class="profile-view__verify-email"
      >
        <Divider variant="secondary" size="small" />
        <Heading :level="4" :semantic-level="2" :margin="false">{{ t('account_verify_email_title') }}</Heading>
        <Prose variant="secondary" size="small">{{ t('account_verify_email_description') }}</Prose>
        <Button
          type="button"
          variant="secondary"
          finish="outlined"
          :disabled="isSendingVerification"
          @click="onSendVerification"
        >
          {{ isSendingVerification ? t('account_verify_email_sending') : t('account_verify_email_button') }}
        </Button>
      </LayoutGap>
      <LayoutGap
        v-if="showSignInMethods && !googleButtonFailed"
        direction="column"
        align="stretch"
        size="xsmall"
        class="profile-view__signin-methods"
      >
        <Divider variant="secondary" size="small" />
        <Heading :level="4" :semantic-level="2" :margin="false">{{ t('account_signin_methods_title') }}</Heading>
        <Prose variant="secondary" size="small">{{ t('account_google_connect_description') }}</Prose>
        <div ref="googleButtonEl" class="profile-view__google-button"></div>
      </LayoutGap>
      <LayoutGap direction="column" align="left" size="xsmall" class="profile-view__danger-zone">
        <Divider variant="secondary" size="small" />
        <Heading :level="4" :semantic-level="2" :margin="false" class="profile-view__danger-title">
          {{ t('account_danger_zone_title') }}
        </Heading>
        <Prose variant="secondary" size="small">{{ t('account_danger_zone_description') }}</Prose>
        <Button
          class="profile-view__delete"
          variant="danger"
          finish="outlined"
          icon-left="trash"
          @click="openDeleteModal"
        >
          {{ t('account_delete_open') }}
        </Button>
      </LayoutGap>
    </LayoutGap>
  </LayoutGap>
  <AppAvatarCropperModal v-model:open="isCropperOpen" :applying="isApplyingAvatar" @apply="onAvatarApply" />
  <Modal v-model="isDeleteModalOpen" :title="t('account_delete_modal_title')" @close="closeDeleteModal">
    <LayoutGap direction="column" align="stretch" size="medium" fill>
      <Prose>{{ t('account_delete_confirm') }}</Prose>
      <LayoutGap
        direction="row"
        align="space-between"
        vertical-align="center"
        size="small"
        fill
        class="profile-view__modal-actions"
      >
        <Button type="button" variant="danger" @click="handleDeleteUser">
          {{ t('account_delete_submit') }}
        </Button>
        <Button type="button" @click="closeDeleteModal">
          {{ t('account_cancel') }}
        </Button>
      </LayoutGap>
    </LayoutGap>
  </Modal>
</template>

<style lang="scss" scoped>
.profile-view {
  max-width: 465px;

  &__content {
    background: theme('color-bg-app-panel');
  }

  &__form {
    width: 100%;
  }

  &__submit {
    margin-top: space('small');
    max-width: 211px;
  }

  &__danger-zone {
    margin-top: space('small');
  }

  &__danger-title {
    color: theme('color-fg-danger');
  }

  &__modal-actions {
    :deep(.button) {
      justify-content: center;
    }
  }
}

.profile-view__google-button {
  display: flex;
  justify-content: center;
  // Reserve the official button's large-size height so the section doesn't
  // jump when GIS finishes rendering.
  min-height: 40px;
}
</style>
